import { useState, type FC } from 'react';
import { CreditCard, Plus, AlertCircle } from 'lucide-react';
import { usePaymentMethods, usePaymentMethodActions } from '../../../hooks/usePaymentMethods';
import { AddPaymentMethodForm } from '../../../components/Dashboard/AddPaymentMethodForm';
import { PaymentCard } from '../../../components/Dashboard/PaymentCard';
import { StripeElementsWrapper } from '../../../contexts/StripeContext';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { cn } from '../../../utils/cn';
import { getBrandDisplay } from '../../../utils/cardBrands';
import type { SavedPaymentMethod } from '../../../api/paymentMethodsApi';

export const PaymentMethodsSection: FC = () => {
  const { data: methods, isLoading, error } = usePaymentMethods();
  const { add, remove, setDefault, isAdding, isSettingDefault } = usePaymentMethodActions();

  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SavedPaymentMethod | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleAddSuccess = async (stripePaymentMethodId: string) => {
    try {
      await add(stripePaymentMethodId);
      setShowAddForm(false);
      setActionError(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add card');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await setDefault(id);
      setActionError(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to set default card');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await remove(deleteTarget.id);
      setDeleteTarget(null);
      setActionError(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove card');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 rounded animate-pulse w-48" />
        {[1, 2].map((i) => (
          <div key={i} className="h-20 bg-gray-200 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <p className="text-red-800">Failed to load payment methods. Please try again.</p>
      </div>
    );
  }

  const deleteMessage = deleteTarget
    ? `Are you sure you want to remove your ${
        getBrandDisplay(deleteTarget.brand).name
      } card ending in ${deleteTarget.last4}?`
    : '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
          <p className="text-sm text-gray-600 mt-1">Manage your saved cards</p>
        </div>
        {!showAddForm && methods && methods.length > 0 && (
          <button
            data-testid="add-card-header"
            onClick={() => setShowAddForm(true)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium',
              'text-white bg-primary-600 rounded-lg',
              'hover:bg-primary-700 transition-colors'
            )}
          >
            <Plus className="w-4 h-4" />
            Add Card
          </button>
        )}
      </div>

      {actionError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}

      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <StripeElementsWrapper>
            <AddPaymentMethodForm
              onSuccess={handleAddSuccess}
              onCancel={() => setShowAddForm(false)}
              isSubmitting={isAdding}
            />
          </StripeElementsWrapper>
        </div>
      )}

      {methods && methods.length > 0 ? (
        <div className="space-y-3">
          {methods.map((method) => (
            <PaymentCard
              key={method.id}
              method={method}
              onSetDefault={() => handleSetDefault(method.id)}
              onRemove={() => setDeleteTarget(method)}
              isSettingDefault={isSettingDefault}
            />
          ))}
        </div>
      ) : (
        !showAddForm && (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No payment methods</h3>
            <p className="text-gray-600 mb-4">Add a card to save it for future bookings</p>
            <button
              data-testid="add-card-empty-state"
              onClick={() => setShowAddForm(true)}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium',
                'text-white bg-primary-600 rounded-lg',
                'hover:bg-primary-700 transition-colors'
              )}
            >
              <Plus className="w-4 h-4" />
              Add Your First Card
            </button>
          </div>
        )
      )}

      <ConfirmDeleteDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title="Remove Payment Method"
        message={deleteMessage}
      />
    </div>
  );
};

export default PaymentMethodsSection;
